/**
 * SupabaseOriginsRepository
 * Responsabilidade: Acesso a dados de origens no Supabase
 * Princípios: Single Responsibility, Dependency Inversion
 */

import { supabase } from '../../lib/supabase';
import { Origin } from '../../types';

export class SupabaseOriginsRepository {
    async findAll(organizationId: string): Promise<Origin[]> {
        const { data, error } = await supabase
            .from('origins')
            .select('id, name, created_at')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[OriginsRepository] Error fetching origins:', error);
            throw new Error(`Erro ao buscar origens: ${error.message}`);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string, organizationId: string): Promise<Origin | null> {
        const { data, error } = await supabase
            .from('origins')
            .select('id, name, created_at')
            .eq('id', id)
            .eq('organization_id', organizationId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            console.error('[OriginsRepository] Error fetching origin:', error);
            throw new Error(`Erro ao buscar origem: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async create(origin: Omit<Origin, 'id' | 'createdAt'>, organizationId: string): Promise<Origin> {
        const { data, error } = await supabase
            .from('origins')
            .insert([{
                organization_id: organizationId,
                name: origin.name
            }])
            .select()
            .single();

        if (error) {
            console.error('[OriginsRepository] Error creating origin:', error);
            throw new Error(`Erro ao criar origem: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async update(id: string, origin: Partial<Origin>, organizationId: string): Promise<Origin> {
        const updateData: any = {};
        if (origin.name !== undefined) updateData.name = origin.name;

        const { data, error } = await supabase
            .from('origins')
            .update(updateData)
            .eq('id', id)
            .eq('organization_id', organizationId)
            .select()
            .single();

        if (error) {
            console.error('[OriginsRepository] Error updating origin:', error);
            throw new Error(`Erro ao atualizar origem: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async delete(id: string, organizationId: string): Promise<void> {
        const { error } = await supabase
            .from('origins')
            .delete()
            .eq('id', id)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[OriginsRepository] Error deleting origin:', error);
            throw new Error(`Erro ao deletar origem: ${error.message}`);
        }
    }

    private mapToEntity(data: any): Origin {
        return {
            id: data.id,
            name: data.name,
            createdAt: data.created_at
        };
    }
}

export const originsRepository = new SupabaseOriginsRepository();
